import { useEffect } from 'react'
// eslint-disable-next-line
import { useBlocker } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { routerSlice } from '@wellesley/router'
import {
  requestChangePath,
  setConfirmLeavePage,
} from '@wellesley/host-remote/api/actions'
import { useSwitchState } from 'hooks/useSwitchState'
import UnsavedChangesModal from 'components/modals/UnsavedChangesModal'

type Props = {
  canShowMessage: boolean
  message?: string
}

export const useModalHandlers = () => useSwitchState()

export const useConfirmLeavePage = ({
  canShowMessage,
  message = 'You have unsaved changes. Are you sure you want to leave?',
}: Props) => {
  const dispatch = useDispatch()
  const path = useSelector(routerSlice.selectors.selectPath)

  const handler = (evt: BeforeUnloadEvent) => {
    evt.preventDefault() // Required for modern browsers
    evt.returnValue = message // Ignored in most modern browsers, but necessary to trigger the dialog

    // Returning a value is still needed for some older browsers
    return message // For older browsers
  }

  useEffect(() => {
    window.onbeforeunload = canShowMessage ? handler : null

    return () => {
      window.onbeforeunload = null
    }
  }, [canShowMessage, message])

  const blocked = useBlocker(canShowMessage)
  const isBlocked = blocked.state === 'blocked'

  const [isOpenConfirmModal, openConfirmModal, closeConfirmModal] =
    useModalHandlers()

  const handleProceed = () => {
    blocked.proceed?.()
    closeConfirmModal()
    dispatch(setConfirmLeavePage(null))
  }

  const handleReset = () => {
    blocked.reset?.()
    closeConfirmModal()
    dispatch(requestChangePath(path))
  }

  const renderConfirm = () => (
    <UnsavedChangesModal
      isOpen={isOpenConfirmModal}
      onDiscard={handleProceed}
      onKeepEditing={handleReset}
    />
  )

  useEffect(() => {
    isBlocked && openConfirmModal()
  }, [isBlocked])

  return renderConfirm
}
